import React, { useState, useRef } from 'react';
import { RotateCcw } from 'lucide-react';

/**
 * CustomSizeSlider - Per-instance size override within the DB span (#754).
 *
 * One dimension (Wuchshöhe or Wuchsbreite) of a placed plant, bounded strictly
 * to the plant's DB range [min, max]. The default position is the DB maximum —
 * the value the plan already uses — so untouched plants keep today's look. A
 * value equal to the DB default is treated as "kein Override" (emits null); any
 * other value marks the instance as abweichend and can be reset to standard.
 *
 * The value is committed on release (pointer up / key up / blur), not on every
 * tick, so a drag produces a single undo step. During the drag the thumb moves
 * locally; 2D ring and 3D mesh update once the override is committed.
 *
 * @param {Object} props
 * @param {string} props.label - Dimension label ("Wuchshöhe" / "Wuchsbreite")
 * @param {number} props.min - Lower bound in cm (DB minimum)
 * @param {number} props.max - Upper bound in cm (DB maximum = default)
 * @param {number|null} props.value - Current override in cm, or null (= DB default)
 * @param {Function} props.onCommit - Called with the new cm value, or null when
 *   the DB default is selected / reset
 */
const CustomSizeSlider = ({ label, min, max, value, onCommit }) => {
  // The plan's effective value when no override is set is the DB maximum.
  const effective = value != null ? value : max;

  const [temp, setTemp] = useState(effective);
  const [prevValue, setPrevValue] = useState(value);

  // Re-sync when the committed value changes externally (undo/redo, reset,
  // selecting another plant) — during render to avoid a setState-in-effect cascade.
  if (value !== prevValue) {
    setPrevValue(value);
    setTemp(value != null ? value : max);
  }

  // Value at the start of an interaction, so a commit produces one undo step.
  const dragStartRef = useRef(effective);

  const commit = () => {
    if (temp === dragStartRef.current) return;
    // Selecting exactly the DB default clears the override (null).
    onCommit(temp === max ? null : temp);
  };

  const handlePointerDown = () => {
    dragStartRef.current = temp;
  };

  const handleChange = (e) => {
    setTemp(parseInt(e.target.value, 10));
  };

  const isOverridden = value != null;
  const fillPercent = max > min ? ((temp - min) / (max - min)) * 100 : 0;
  const sliderId = `custom-size-${label}`;

  return (
    <div className="w-full">
      <div className="flex items-baseline justify-between mb-1">
        <label htmlFor={sliderId} className="text-xs font-medium text-gray-600">
          {label}
        </label>
        <span className="text-sm font-semibold text-emerald-600">{temp} cm</span>
      </div>

      <input
        type="range"
        id={sliderId}
        min={min}
        max={max}
        step="1"
        value={temp}
        onChange={handleChange}
        onPointerDown={handlePointerDown}
        onPointerUp={commit}
        onKeyDown={handlePointerDown}
        onKeyUp={commit}
        onBlur={commit}
        style={{
          background: `linear-gradient(to right, #10b981 0%, #10b981 ${fillPercent}%, #e5e7eb ${fillPercent}%, #e5e7eb 100%)`,
        }}
        className="w-full h-2 rounded-full appearance-none cursor-pointer slider-emerald"
        aria-label={`${label} für diese Pflanze anpassen`}
        aria-valuetext={`${temp} cm`}
      />

      <div className="flex items-center justify-between mt-1">
        <span className="text-[10px] text-gray-500">
          {isOverridden ? `abweichend · Standard ${max} cm` : `Standard (${min}–${max} cm)`}
        </span>
        {isOverridden && (
          <button
            onClick={() => onCommit(null)}
            className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700"
            title={`${label} auf Standard zurücksetzen`}
          >
            <RotateCcw size={12} />
            Zurücksetzen
          </button>
        )}
      </div>
    </div>
  );
};

export default React.memo(CustomSizeSlider);
